import { createAsyncThunk } from "@reduxjs/toolkit";
import { validateFields, closeRequestContactUpdateDialog, openSuccessDialog } from "@requestContactUpdate/store/ducks";
import { requestContactUpdateSelector } from "@requestContactUpdate/store/selectors";
import { openNotificationThunk } from "@store/Notification/thunks";
import { ThunkApiConfig } from "@shared/models/redux";

interface AttachmentPayload {
  name: string;
  base64: string;
}

/**
 * BREAKING (LPC-10865): legacy `attachment`/`attachmentName` REMOVED — body now sends `attachments[]`.
 * contact-service-api (LPC-10934) must support the new shape BEFORE this UI is promoted.
 */
export interface RequestContactUpdateBody {
  requestType: string;
  request: string;
  entitledAccounts: string[];
  entitledEntities: string[];
  /** Multi-attachment list (LPC-10865). Up to 4 files; 25 MB combined total. */
  attachments?: AttachmentPayload[];
  customFields?: { [key: string]: any };
}

export const sendRequestContactUpdateThunk = createAsyncThunk<boolean, RequestContactUpdateBody, ThunkApiConfig>(
  "requestContactUpdate/sendRequestContactUpdate",
  async (body, { rejectWithValue, dispatch, extra }) => {
    try {
      // LPC-10934: now creates a Salesforce Case directly via contact-service-api (with
      // contactManagedBy queue routing), replacing the old S3-pickup path on entitlement-svc.
      const response = await extra<{ caseId: string }, RequestContactUpdateBody>(
        "contacts/v1/user/RequestContactUpdate",
        { method: "post", body }
      );

      if (response.data?.caseId) {
        dispatch(closeRequestContactUpdateDialog());
        dispatch(openSuccessDialog());

        return true;
      }

      return false;
    } catch (error: any) {
      // Backend error shape varies: plain string (contact-service-api BadRequest("…")),
      // array (legacy entitlement-svc), or {error}/{message} object. Handle all three
      // so the user sees the real message instead of the string's first character.
      const data = error.response?.data;
      const errorMessage =
        (typeof data === "string" && data) ||
        (Array.isArray(data) && data[0]) ||
        data?.error ||
        data?.message ||
        "Some error occurred. Please try again.";

      dispatch(
        openNotificationThunk({
          variant: "warning",
          text: errorMessage,
        })
      );

      return rejectWithValue(errorMessage);
    }
  }
);

export const submitFormThunk = createAsyncThunk<void, void, ThunkApiConfig>(
  "requestContactUpdate/submitFormThunk",
  // LPC-10991: must await the inner network thunk so `loading` stays true
  // for the whole submit lifecycle and blocks duplicate-click case creation.
  async (_, { dispatch, getState }) => {
    dispatch(validateFields());

    const { requestType, commonFields, customFields, validationWarningShown } = requestContactUpdateSelector(
      getState()
    );

    if (validationWarningShown) return;

    const fallbackMessage = "Request Contact Update - Self Service";

    const message =
      commonFields.message && commonFields.message.trim().length > 0 ? commonFields.message : fallbackMessage;

    const contactName = (commonFields.contactName || "").trim();
    const contactEmail = (commonFields.contactEmail || "").trim();
    /** LPC-10619 description line: "Request for Changes To: [Name], [Email]" */
    const RequestForChangesTo = `: ${contactName}, ${contactEmail}`;

    const body: RequestContactUpdateBody = {
      customFields: {
        ...customFields,
        RequestForChangesTo,
      },
      requestType: requestType || "",
      request: message,
      entitledAccounts: commonFields.selectedAccounts,
      entitledEntities: commonFields.selectedFunds,
    };

    // LPC-10865: send multi-attachment list (omit the field entirely when there are no attachments
    // so the backend falls through to its no-op path cleanly).
    if (commonFields.attachments.length > 0) {
      body.attachments = commonFields.attachments.map((a) => ({
        name: a.name,
        base64: a.base64,
      }));
    }

    await dispatch(sendRequestContactUpdateThunk(body));
  }
);
